/*!
 * Checkout Guard
 *
 * Detects scripts that hijack the Shopify checkout button and send shoppers to a lookalike
 * checkout to steal card details (October 2026 HulkApps / theme app extension attack:
 * https://romanzipp.com/blog/shopify-hit-with-phishing-attack). When detected it reports to
 * the support portal, which emails the team, and it blocks redirects to known phishing domains
 * and to any .top domain (every phishing domain seen in this campaign so far has been .top).
 *
 * INSTALL (per store theme)
 *   1. Online Store > Themes > Edit code > Assets > Add a new asset > upload this file as checkout-guard.js
 *   2. In layout/theme.liquid add this as the FIRST thing inside <head>, before {{ content_for_header }}
 *      so it loads before any app scripts:
 *        {% # theme-check-disable ParserBlockingScript %}
 *        <script src="{{ 'checkout-guard.js' | asset_url }}"></script>
 *        {% # theme-check-enable ParserBlockingScript %}
 *      It is deliberately parser blocking so it runs before any app script can tamper with window.open / sendBeacon.
 *      Adding defer also works (checkout click detection doesn't depend on load order) but loses that guarantee.
 *
 * OPTIONAL CONFIG - add before the script tag above:
 *   <script>
 *     window.CheckoutGuardConfig = {
 *       block: true,                       // also block redirects to ANY unrecognised site after checkout click (default: report only)
 *       allowedHosts: ['example-pay.com'], // extra legitimate off-site checkout/payment hosts
 *       blockedHosts: ['bad-site.example'], // extra domains to always block (subdomains included)
 *       blockedTlds: ['xyz'],              // extra top level domains to always block (.top is built in)
 *       debug: true                        // log what it sees to the console
 *     };
 *   </script>
 *
 * TEST: open the store in a private window, add to cart, open the browser console and run
 *   CheckoutGuard.test()
 * which sends a test report (reason "offsite_checkout_redirect", destination test.invalid).
 */
(function () {
  'use strict';

  if (window.CheckoutGuard) {
    return;
  }

  var config = window.CheckoutGuardConfig || {};
  var ENDPOINT = config.endpoint || 'https://support.lay-z-spa.co.uk/api/security/checkout-alert';
  var BLOCK_UNKNOWN = config.block === true;
  var DEBUG = config.debug === true;

  // How long after a checkout click an off-site navigation is treated as suspicious.
  var CHECKOUT_INTENT_MS = 15000;

  // Indicators from the October 2026 campaign. Navigation to these is always blocked, at any time.
  // The attackers keep registering new .top domains (biliing.top, checkout-guard.top, ...) so the whole TLD is blocked.
  var KNOWN_BAD_TLDS = ['top'].concat(config.blockedTlds || []);
  var KNOWN_BAD_HOSTS = [
    'biliing.top',
    'checkout-confirm.top',
    'order-confirm.top',
    'checkout-guard.top'
  ].concat(config.blockedHosts || []);
  var KNOWN_BAD_STORAGE_KEYS = ['__ctf_shim_handoff_done_v1', '__ctf_checkout_txn_v1'];
  var KNOWN_BAD_WINDOW_NAME = /^HLK\d*:/;

  // Legitimate places a checkout click can go. The store's own domain is added below.
  var ALLOWED_HOSTS = [
    'shopify.com',
    'myshopify.com',
    'shop.app',
    'shopifycs.com',
    'paypal.com',
    'klarna.com',
    'clearpay.co.uk',
    'afterpay.com'
  ].concat(config.allowedHosts || []);

  var CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href*="/checkout"]',
    'form[action*="/checkout"] [type="submit"]',
    '#checkout',
    '.cart__checkout-button',
    '.shopify-cart-checkout',
    '[data-checkout-button]'
  ].join(',');

  // Keep our own references in case another script swaps these out later.
  var nativeBeacon = navigator.sendBeacon ? navigator.sendBeacon.bind(navigator) : null;
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var nativeOpen = window.open;
  var ownScript = (document.currentScript && document.currentScript.src) || null;

  var shopHost = location.hostname.toLowerCase().replace(/^www\./, '');
  ALLOWED_HOSTS.push(shopHost);

  var lastCheckoutIntent = 0;
  var evidence = [];
  var reported = {};

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[checkout-guard]'].concat([].slice.call(arguments)));
    }
  }

  function hostMatches(host, domain) {
    domain = String(domain).toLowerCase().replace(/^www\./, '');
    return host === domain || host.slice(-(domain.length + 1)) === '.' + domain;
  }

  function hasBadTld(host) {
    var tld = host.slice(host.lastIndexOf('.') + 1);
    for (var i = 0; i < KNOWN_BAD_TLDS.length; i++) {
      if (tld === String(KNOWN_BAD_TLDS[i]).toLowerCase().replace(/^\./, '')) {
        return true;
      }
    }
    return false;
  }

  function matchesAny(host, list) {
    for (var i = 0; i < list.length; i++) {
      if (hostMatches(host, list[i])) {
        return true;
      }
    }
    return false;
  }

  function cleanStack(stack) {
    var lines = String(stack || '')
      .split('\n')
      .map(function (line) { return line.trim(); })
      .filter(Boolean);

    // Chrome starts with an "Error" line, Firefox/Safari don't. Then the top frame is always our own wrapper.
    if (lines[0] && lines[0].indexOf('Error') === 0) {
      lines.shift();
    }
    lines.shift();

    return lines
      // Match our exact script URL only - the attackers use lookalike names such as checkout-guard.top.
      .filter(function (line) { return !ownScript || line.indexOf(ownScript) === -1; })
      .slice(0, 4)
      .join(' <- ');
  }

  function addEvidence(text) {
    if (evidence.length < 8 && evidence.indexOf(text) === -1) {
      evidence.push(text);
      log('evidence', text);
    }
  }

  // ---- Reporting -------------------------------------------------------------------------

  function report(reason, url, detail) {
    var key = reason + '|' + (url ? url.hostname : '');

    if (reported[key]) {
      return;
    }
    reported[key] = true;

    try {
      if (sessionStorage.getItem('__checkout_guard_reported:' + key)) {
        return;
      }
      sessionStorage.setItem('__checkout_guard_reported:' + key, '1');
    } catch (e) { /* storage blocked - still report */ }

    var notes = evidence.slice();
    if (detail) {
      notes.unshift(detail);
    }

    // Only origin + path are sent: the hijack puts the shopper's cart in the query string.
    var params = new URLSearchParams();
    params.set('shop', location.hostname);
    params.set('reason', reason);
    params.set('page', location.origin + location.pathname);
    if (url) {
      params.set('destination', url.origin + url.pathname);
    }
    params.set('evidence', notes.join('\n').slice(0, 4900));

    if (window.console) {
      console.warn('[checkout-guard] ' + reason + (url ? ' -> ' + url.origin + url.pathname : ''));
    }

    try {
      // A form-encoded beacon is a "simple" request, so no CORS preflight, and it survives page unload.
      if (nativeBeacon && nativeBeacon(ENDPOINT, params)) {
        return;
      }
    } catch (e) { /* fall through to fetch */ }

    try {
      if (nativeFetch) {
        nativeFetch(ENDPOINT, { method: 'POST', body: params, mode: 'no-cors', keepalive: true, credentials: 'omit' });
      }
    } catch (e) { /* nothing more we can do */ }
  }

  // ---- 1. Known indicators left behind by the hijack script ------------------------------

  function checkIndicators() {
    var found = [];

    KNOWN_BAD_STORAGE_KEYS.forEach(function (key) {
      try {
        if (sessionStorage.getItem(key) !== null || localStorage.getItem(key) !== null) {
          found.push('storage key ' + key);
        }
      } catch (e) { /* storage blocked */ }
    });

    try {
      if (KNOWN_BAD_WINDOW_NAME.test(window.name)) {
        found.push('window.name carries a cart payload (' + window.name.slice(0, 7) + '...)');
        window.name = ''; // it holds the shopper's cart, don't let it travel to the next page
      }
    } catch (e) { /* ignore */ }

    if (found.length) {
      report('known_indicator', null, 'Indicators: ' + found.join(', '));
    }
  }

  // ---- 2. Watch checkout clicks, and who cancels them ------------------------------------

  function isCheckoutEvent(e) {
    var target = e.target;

    if (e.type === 'submit') {
      var action = (target && target.getAttribute && target.getAttribute('action')) || '';
      var submitter = e.submitter;
      return /\/checkouts?\b/.test(action) || !!(submitter && submitter.name === 'checkout');
    }

    if (target && target.nodeType === 3) {
      target = target.parentElement;
    }

    return !!(target && target.closest && target.closest(CHECKOUT_SELECTOR));
  }

  function onCheckoutIntent(e) {
    if (!isCheckoutEvent(e)) {
      return;
    }

    lastCheckoutIntent = Date.now();
    try { e.__checkoutGuard = true; } catch (err) { /* ignore */ }
    log('checkout intent', e.type);

    // The hijack sets its storage latch during the click, before its redirect.
    setTimeout(checkIndicators, 250);
    setTimeout(checkIndicators, 2500);
  }

  // Window capture listeners run before the document capture listeners the hijack uses.
  ['pointerdown', 'touchstart', 'mousedown', 'click', 'submit'].forEach(function (type) {
    window.addEventListener(type, onCheckoutIntent, { capture: true, passive: true });
  });

  // Record which script cancels a checkout click. A record alone is not reported, but it is
  // attached to any report so we can see which app or file did it.
  ['preventDefault', 'stopPropagation', 'stopImmediatePropagation'].forEach(function (method) {
    var original = Event.prototype[method];

    if (typeof original !== 'function') {
      return;
    }

    Event.prototype[method] = function () {
      try {
        if (this.__checkoutGuard) {
          addEvidence(method + '() on checkout ' + this.type + ' from: ' + (cleanStack(new Error().stack) || 'unknown (inline/eval script)'));
        }
      } catch (e) { /* never break the page */ }

      return original.apply(this, arguments);
    };
  });

  // ---- 3. Catch the redirect itself ------------------------------------------------------

  // Returns true when the navigation should be blocked.
  function inspectDestination(url, kind) {
    if (!/^https?:$/.test(url.protocol)) {
      return false;
    }

    var host = url.hostname.toLowerCase().replace(/\.$/, '');
    var knownBad = hasBadTld(host) || matchesAny(host, KNOWN_BAD_HOSTS);
    var afterCheckout = Date.now() - lastCheckoutIntent < CHECKOUT_INTENT_MS;

    if (!knownBad && (!afterCheckout || matchesAny(host, ALLOWED_HOSTS))) {
      return false;
    }

    checkIndicators();

    if (knownBad) {
      report('known_bad_destination', url, kind + ' to blocked domain ' + host + ' was blocked');
      return true;
    }

    report(kind === 'popup' ? 'offsite_checkout_popup' : 'offsite_checkout_redirect', url,
      kind + ' to unrecognised host ' + (BLOCK_UNKNOWN ? 'was blocked' : 'was allowed (report-only mode)'));

    return BLOCK_UNKNOWN;
  }

  function sendToRealCheckout() {
    try { window.name = ''; } catch (e) { /* ignore */ }
    setTimeout(function () { location.assign('/checkout'); }, 0);
  }

  // The Navigation API sees location.href / assign / replace, link clicks and form posts.
  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', function (e) {
      var url;
      try { url = new URL(e.destination.url); } catch (err) { return; }

      if (inspectDestination(url, 'redirect') && e.cancelable) {
        e.preventDefault();
        sendToRealCheckout();
      }
    });
  } else {
    log('Navigation API not supported in this browser; redirect detection limited to indicators and popups');
  }

  if (typeof nativeOpen === 'function') {
    window.open = function (target) {
      try {
        if (target && inspectDestination(new URL(String(target), location.href), 'popup')) {
          sendToRealCheckout();
          return null;
        }
      } catch (e) { /* invalid URL - let the browser deal with it */ }

      return nativeOpen.apply(this, arguments);
    };
  }

  // ---- Start ------------------------------------------------------------------------------

  checkIndicators();
  document.addEventListener('DOMContentLoaded', checkIndicators);

  window.CheckoutGuard = {
    version: '1.0.0',
    test: function () {
      delete reported['offsite_checkout_redirect|test.invalid'];
      try { sessionStorage.removeItem('__checkout_guard_reported:offsite_checkout_redirect|test.invalid'); } catch (e) { /* ignore */ }
      report('offsite_checkout_redirect', new URL('https://test.invalid/test/'), 'TEST REPORT - triggered manually from the browser console');
      return 'Test report sent to ' + ENDPOINT;
    }
  };
})();
